import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/context/AuthContext";
import Pouch from "@/components/Pouch";
import CoffeeBeans from "@/components/CoffeeBeans";

const HERO = "https://images.unsplash.com/photo-1586095516671-d085ff58cdd4?auto=format&fit=crop&w=2000&q=85";
const TEXTURE = "https://images.unsplash.com/photo-1676921820173-33e5e4d20aee?auto=format&fit=crop&w=1600&q=80";

export default function Landing() {
  const [featured, setFeatured] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    axios.get(`${API}/blends?featured=true`).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[640px] flex items-end overflow-hidden">
        <img src={HERO} alt="Ripe coffee cherries on the estate" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "contrast(1.15) saturate(1.15) brightness(0.85)" }} data-testid="hero-image" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,10,6,0.55) 0%, rgba(5,10,6,0.15) 35%, rgba(5,10,6,0.70) 70%, rgba(5,10,6,0.97) 100%), linear-gradient(90deg, rgba(5,10,6,0.85) 0%, rgba(5,10,6,0.2) 60%, transparent 100%)" }} />
        <div className="divine-halo" style={{ left: "28%", top: "62%" }} />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 30% 70%, rgba(229,56,63,0.16), transparent 55%), radial-gradient(ellipse at 75% 30%, rgba(92,203,133,0.14), transparent 55%)",
          }}
        />

        <CoffeeBeans zIndex={4} />

        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 pb-24 w-full fade-up">
          <div className="label-mono mb-6 text-[var(--accent)]" data-testid="hero-chapter">॥ Ch. 01 — From the Estate ॥</div>
          <div className="divine-tagline text-lg md:text-2xl mb-4" data-testid="hero-divine-tagline">A coffee with divine energy.</div>
          <h1 className="font-sacred text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-4xl embossed-light">
            COFFEE, <span className="embossed">COMPOSED</span> <br /> BY HAND.
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-12 mt-10 gap-8 items-end">
            <p className="md:col-span-6 text-[var(--text-secondary)] text-lg leading-relaxed max-w-xl">
              Small-batch house blends and one open canvas. Every bag is
              <span className="text-ember font-medium"> hand-roasted </span>
              to order in Shimoga, Karnataka — then ground the way you brew it.
            </p>
            <div className="md:col-span-6 flex flex-wrap items-center gap-4 justify-start md:justify-end">
              <button onClick={() => nav("/shop")} className="btn-primary" data-testid="hero-shop-btn">Shop the Roastery</button>
              <button onClick={() => nav("/blend")} className="btn-highlight" data-testid="hero-blend-btn">Build Your Blend</button>
            </div>
          </div>
        </div>
      </section>

      {/* STRIP */}
      <section className="roast-panel border-y border-[var(--border)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center justify-between gap-4">
          {[
            { icon: "☕", t: "Roasted within 72h" },
            { icon: "◉", t: "Flat ₹80 shipping across India" },
            { icon: "❖", t: "Origin-traced beans" },
            { icon: "▲", t: "Grind to your brewer" },
          ].map((s) => (
            <span key={s.t} className="label-mono text-[var(--text-secondary)] flex items-center gap-2">
              <span className="text-[var(--accent)]">{s.icon}</span>
              {s.t}
            </span>
          ))}
        </div>
      </section>

      {/* LAUNCH OFFER — parchment coupon */}
      <section className="relative overflow-hidden py-14 md:py-20" data-testid="launch-offer-banner">
        <CoffeeBeans density="sparse" zIndex={2} />
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-12">
          <div
            className="relative offer-pulse overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, #0F2016 0%, #132A1D 45%, #1A0E10 100%)",
              border: "1px dashed var(--border-strong)",
              padding: "36px 24px",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06), 0 0 40px rgba(229,56,63,0.12), var(--shadow-warm)",
            }}
          >
            {/* Rotating leaf ornament */}
            <div className="absolute -top-6 -right-6 opacity-30 pointer-events-none">
              <svg width="120" height="120" viewBox="0 0 120 120">
                <g fill="none" stroke="#5CCB85" strokeWidth="1.2">
                  <ellipse cx="60" cy="60" rx="44" ry="26" transform="rotate(-30 60 60)" />
                  <ellipse cx="60" cy="60" rx="44" ry="26" transform="rotate(30 60 60)" />
                  <circle cx="60" cy="60" r="6" fill="#E5383F" />
                </g>
              </svg>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center relative z-10">
              <div className="md:col-span-8 text-center md:text-left">
                <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-[var(--accent)] mb-3 badge-blink">
                  <span className="whitespace-nowrap">॥ LIMITED-TIME</span> <span className="whitespace-nowrap">· LAUNCH OFFER ॥</span>
                </div>
                <h2 className="font-sacred text-4xl md:text-6xl leading-[0.95] tracking-tight embossed">
                  <span className="text-ember">5% OFF</span> ALL BLENDS
                </h2>
                <p className="mt-4 font-mono text-xs tracking-[0.25em] uppercase text-[var(--text-secondary)]">
                  Celebrating the launch of Rudra Roast · Auto-applied at checkout
                </p>
              </div>

              <div className="md:col-span-4 flex flex-col items-center md:items-end gap-3">
                {/* Kraft coupon chip */}
                <div
                  className="relative px-6 py-3"
                  style={{
                    background: "var(--surface)",
                    border: "2px dashed var(--primary)",
                    fontFamily: "'IBM Plex Mono', monospace",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  }}
                >
                  <div className="text-[9px] tracking-[0.3em] text-[var(--text-muted)]">USE CODE</div>
                  <div className="text-2xl font-bold tracking-[0.15em] text-[var(--primary)]">RUDRA5</div>
                </div>
                <button
                  onClick={() => nav("/shop")}
                  data-testid="launch-offer-cta"
                  className="btn-primary"
                >
                  Claim the Offer →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FLASHING SLOGAN BANNER — parchment ticker */}
      <section
        className="relative border-y border-[var(--border)] overflow-hidden"
        style={{ background: "linear-gradient(180deg, var(--surface-warm), var(--surface-alt))" }}
        data-testid="flashing-slogan-banner"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 50% 50%, rgba(229,56,63,0.14), transparent 65%)" }}
        />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 py-10 md:py-14 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="inline-block w-2 h-2 rounded-full bg-[var(--accent)] badge-blink" />
            <span className="label-mono text-[var(--accent)] text-[10px] badge-blink">॥ NEW · SIGNATURE SERVICE ॥</span>
            <span className="inline-block w-2 h-2 rounded-full bg-[var(--accent)] badge-blink" />
          </div>
          <h2 className="slogan-hd slogan-flash-anim text-[32px] md:text-[64px] lg:text-[80px] leading-[0.95] tracking-tight">
            CUSTOMISED BLEND COFFEE
          </h2>
          <p className="slogan-hd-sub text-xl md:text-3xl mt-4">
            ( blend coffee of your own )
          </p>
          <button
            onClick={() => nav("/blend")}
            className="btn-primary mt-8"
            data-testid="flashing-slogan-cta"
          >
            Start Composing →
          </button>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="flex items-end justify-between mb-16">
          <div>
            <div className="label-mono mb-4 text-[var(--accent)]">॥ Ch. 02 — House Blends ॥</div>
            <h2 className="font-sacred text-4xl md:text-6xl leading-none tracking-tight embossed-light">THIS MONTH&apos;S ROAST LOG.</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-block label-mono link-underline">All Blends →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {featured.map((b, i) => (
            <Link
              key={b.id}
              to={`/shop/${b.id}`}
              className="group block fade-up card-flat p-4 relative"
              style={{ animationDelay: `${i * 100}ms` }}
              data-testid={`featured-blend-${i}`}
            >
              {b.most_popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
                  <div
                    className="px-4 py-1.5 whitespace-nowrap"
                    style={{
                      background:
                        "linear-gradient(180deg, #FF5C5F 0%, #E5383F 60%, #A3152A 100%)",
                      color: "#FFFFFF",
                      fontFamily: "'Fraunces', serif",
                      fontSize: "10px",
                      fontWeight: 700,
                      letterSpacing: "0.2em",
                      border: "1px solid #7A0F1F",
                      boxShadow: "0 1px 0 rgba(255,240,210,0.4) inset, 0 4px 12px rgba(0,0,0,0.25)",
                    }}
                  >
                    ★ MOST POPULAR ★
                  </div>
                </div>
              )}
              <div className="aspect-[4/5] overflow-hidden" style={{ background: "var(--surface-warm)" }}>
                <div className="w-full h-full transition-transform duration-500 group-hover:scale-105">
                  <Pouch blend={b} />
                </div>
              </div>
              <div className="mt-6 flex justify-between items-start">
                <div>
                  <div className="label-mono mb-2 text-[var(--text-muted)]">{b.origin}</div>
                  <div className="font-serif text-2xl embossed-light">{b.name}</div>
                  <div className="text-[var(--text-secondary)] mt-1 text-sm">{b.flavor_notes.join(" · ")}</div>
                </div>
                <div className="font-mono text-sm text-[var(--primary-hover)]">₹{b.price_inr}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CUSTOM BLEND SLOGAN */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="custom-blend-slogan">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 20% 30%, rgba(229,56,63,0.12), transparent 55%), radial-gradient(ellipse at 80% 70%, rgba(92,203,133,0.14), transparent 55%)",
          }}
        />
        {/* Marquee whisper */}
        <div className="absolute top-8 left-0 right-0 overflow-hidden opacity-[0.08] pointer-events-none select-none">
          <div className="marquee-inner font-sacred text-[110px] leading-none whitespace-nowrap text-[var(--primary)]">
            <span>CUSTOMISED BLEND · CUSTOMISED BLEND · CUSTOMISED BLEND · CUSTOMISED BLEND ·&nbsp;</span>
            <span aria-hidden>CUSTOMISED BLEND · CUSTOMISED BLEND · CUSTOMISED BLEND · CUSTOMISED BLEND ·&nbsp;</span>
          </div>
        </div>

        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 text-center">
          <div className="label-mono mb-6 text-[var(--accent)]">॥ Signature Craft ॥</div>
          <div className="slogan-frame inline-block px-4 md:px-16">
            <h2 className="slogan-hd text-[42px] md:text-[84px] lg:text-[104px] leading-[0.95] tracking-tight">
              CUSTOMISED BLEND COFFEE
            </h2>
          </div>
          <p className="slogan-hd-sub text-2xl md:text-4xl mt-8">
            ( blend coffee of your own )
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <button onClick={() => nav("/blend")} className="btn-primary" data-testid="slogan-blend-cta">
              Compose Your Cup
            </button>
            <button onClick={() => nav("/shop")} className="btn-outline" data-testid="slogan-shop-cta">
              Or Pick a House Blend
            </button>
          </div>
          {/* Ingredient tags — natural palette */}
          <div className="mt-10 flex justify-center gap-6 md:gap-10 flex-wrap font-mono text-[11px] tracking-[0.3em]">
            <span className="text-[#FF5C5F]">● ARABICA</span>
            <span className="text-[#C79A6B]">● ROBUSTA</span>
            <span className="text-[#5CCB85]">● CHICORY</span>
          </div>
        </div>
      </section>

      {/* SLOW · DARK · SMALL-BATCH strip — espresso-dark panel */}
      <section className="relative overflow-hidden espresso-panel my-10">
        <CoffeeBeans density="sparse" zIndex={2} />
        <div className="absolute top-4 left-1/2 -translate-x-1/2 opacity-70 font-sacred text-[10px] tracking-[0.4em]" style={{color:"#C8D4C2"}}>
          ॥ SLOW · HAND-CRAFTED · SMALL-BATCH ॥
        </div>
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
            <div className="text-center md:text-left">
              <div className="font-sacred text-6xl mb-3" style={{color:"#5CCB85"}}>01</div>
              <div className="label-mono mb-2" style={{color:"#C8D4C2"}}>Sourced</div>
              <p className="text-sm leading-relaxed" style={{color:"rgba(244,238,221,0.75)"}}>
                Direct from estates across Chikmagalur, Coorg, Wayanad and the Nilgiris. Traceable to the tree.
              </p>
            </div>
            <div className="text-center md:text-left border-t md:border-t-0 md:border-l pt-8 md:pt-0 md:pl-10" style={{borderColor:"rgba(92,203,133,0.25)"}}>
              <div className="font-sacred text-6xl mb-3" style={{color:"#5CCB85"}}>02</div>
              <div className="label-mono mb-2" style={{color:"#C8D4C2"}}>Roasted Fresh</div>
              <p className="text-sm leading-relaxed" style={{color:"rgba(244,238,221,0.75)"}}>
                12-minute drum roasts at 220°C. First and second crack. Deep, syrupy body. Chocolate & jaggery notes.
              </p>
            </div>
            <div className="text-center md:text-left border-t md:border-t-0 md:border-l pt-8 md:pt-0 md:pl-10" style={{borderColor:"rgba(92,203,133,0.25)"}}>
              <div className="font-sacred text-6xl mb-3" style={{color:"#5CCB85"}}>03</div>
              <div className="label-mono mb-2" style={{color:"#C8D4C2"}}>Shipped Warm</div>
              <p className="text-sm leading-relaxed" style={{color:"rgba(244,238,221,0.75)"}}>
                One-way valve pouches within 72h of order. 45 days of peak flavour, right to your kitchen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STORY strip */}
      <section className="relative border-y border-[var(--border)] overflow-hidden">
        <img src={TEXTURE} alt="Coffee cherries" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(5,10,6,0.95) 0%, rgba(5,10,6,0.75) 50%, rgba(5,10,6,0.9) 100%)" }} />
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="label-mono mb-4 text-[var(--accent)]">॥ Ch. 03 — The Craft ॥</div>
            <h2 className="font-sacred text-3xl md:text-5xl leading-tight embossed-light">EVERY GRAM IS A DECISION.</h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 text-[var(--text-secondary)] leading-relaxed text-lg space-y-6">
            <p>We source directly from estates in Chikmagalur, Coorg, Wayanad, Araku and the Nilgiris. Green beans are cupped, weighed, and blended by our head roaster before they meet the drum.</p>
            <p>Your bag is roasted the day it ships. No sitting on shelves. No wholesale-scale batches. Just fresh, considered coffee.</p>
            <Link to="/story" className="inline-block label-mono link-underline text-[var(--primary)]" data-testid="story-cta">Read the full story →</Link>
          </div>
        </div>
      </section>

      {/* CUSTOM BLEND CTA */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="label-mono mb-4 text-[var(--accent)]">॥ Ch. 04 — Bespoke ॥</div>
            <h2 className="font-sacred text-4xl md:text-6xl leading-none tracking-tight embossed-light">DESIGN YOUR OWN <span className="embossed">SIGNATURE</span>.</h2>
            <p className="mt-6 text-[var(--text-secondary)] text-lg max-w-lg leading-relaxed">
              Choose your Arabica-to-Robusta-to-Chicory ratio, roast depth, grind size and weight. Our roaster hand-blends and roasts it fresh.
            </p>
            <button onClick={() => nav("/blend")} className="btn-primary mt-8" data-testid="landing-blend-cta">Open the Blend Builder</button>
          </div>
          <div className="md:col-span-6 sacred-frame p-8 md:p-10 bg-[var(--surface)]">
            <div className="label-mono mb-6">Preview · Blend Composition</div>
            <div className="space-y-6">
              <Row label="Arabica" value="55%" bar={55} />
              <Row label="Robusta" value="25%" bar={25} />
              <Row label="Chicory" value="20%" bar={20} />
              <Row label="Roast" value="Medium-Dark" />
              <Row label="Grind" value="Fine (Filter)" />
              <Row label="Weight" value="500 g" />
            </div>
            <div className="mt-8 pt-6 border-t border-[var(--border)] flex justify-between items-baseline">
              <span className="label-mono">Est. Price</span>
              <span className="font-mono text-3xl text-[var(--primary)]">₹525</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Row({ label, value, bar }) {
  return (
    <div>
      <div className="flex justify-between items-baseline mb-2">
        <span className="label-mono">{label}</span>
        <span className="font-mono text-sm text-[var(--text)]">{value}</span>
      </div>
      {typeof bar === "number" && (
        <div className="h-[2px] bg-[var(--border)] w-full">
          <div className="h-full bg-[var(--primary)]" style={{ width: `${bar}%` }} />
        </div>
      )}
    </div>
  );
}
